import { useState } from 'react';
import { ScreenFrame } from '@/components/Screen';
import { EntityCard, EntityList, SectionCard } from '@/components/DataList';
import { support } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  Form,
  Input,
  Loading,
  StatusBadge,
  Textarea,
} from '@/components/ui';
import { formatDateTime, required, str } from '@/utils/format';

export function SupportScreen({ mode = 'customer' }: { mode?: 'admin' | 'customer' }) {
  const isAdmin = mode === 'admin';
  const state = useAsync(
    () => support.tickets(isAdmin ? { all: 1 } : undefined),
    (p) => pickList(p, 'tickets', 'items', 'data'),
    [isAdmin],
  );

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [active, setActive] = useState<JsonMap | null>(null);
  const [messages, setMessages] = useState<JsonMap[]>([]);
  const [reply, setReply] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function createTicket() {
    const miss = required({ subject, body }, ['subject', 'body']);
    if (miss) {
      setMsg(miss);
      return;
    }
    setBusy(true);
    setMsg('');
    const res = await support.createTicket({ subject, message: body, body });
    setBusy(false);
    if (!res.success) {
      setMsg(errorMessage(res));
      return;
    }
    setSubject('');
    setBody('');
    setMsg('تم فتح التذكرة.');
    state.reload();
  }

  async function openTicket(ticket: JsonMap) {
    setActive(ticket);
    setReply('');
    const id = ticket.id ?? ticket.ticket_id;
    const res = await support.messages(String(id));
    if (res.success) setMessages(pickList(res, 'messages', 'items', 'data'));
    else setMessages([]);
  }

  async function sendReply() {
    if (!active) return;
    const miss = required({ reply }, ['reply']);
    if (miss) {
      setMsg(miss);
      return;
    }
    setBusy(true);
    const res = await support.reply({
      ticket_id: active.id ?? active.ticket_id,
      message: reply,
    });
    setBusy(false);
    if (!res.success) {
      setMsg(errorMessage(res));
      return;
    }
    setReply('');
    void openTicket(active);
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  const tickets = state.data ?? [];

  return (
    <ScreenFrame
      mode={mode}
      icon="support"
      title={isAdmin ? 'محادثات الدعم' : 'الدعم الفني'}
      subtitle={
        isAdmin
          ? 'محادثات الدعم مع الزبائن ومتابعة التذاكر المفتوحة.'
          : 'افتح تذكرة جديدة أو تابع ردود فريق الدعم.'
      }
      actions={
        <Button type="button" variant="secondary" onClick={() => state.reload()}>
          تحديث
        </Button>
      }
    >
      {!isAdmin ? (
        <SectionCard title="تذكرة جديدة" subtitle="اكتب موضوعاً واضحاً لتسريع الرد.">
          <Form onSubmit={() => void createTicket()}>
            <Input label="الموضوع" value={subject} onChange={(e) => setSubject(e.target.value)} required />
            <Textarea label="الرسالة" value={body} onChange={setBody} required />
            {msg ? <p className={msg.includes('تم') ? 'ok' : 'error'}>{msg}</p> : null}
            <div className="action-row wrap">
              <Button type="submit" disabled={busy}>
                إرسال
              </Button>
            </div>
          </Form>
        </SectionCard>
      ) : msg ? (
        <p className={msg.includes('تم') ? 'ok' : 'error'}>{msg}</p>
      ) : null}

      <SectionCard
        title={isAdmin ? `كل التذاكر (${tickets.length})` : 'تذاكري'}
        subtitle={isAdmin ? 'اضغط تذكرة لفتح المحادثة والرد.' : undefined}
      >
        {tickets.length === 0 ? (
          <Empty title="لا توجد تذاكر" hint={isAdmin ? 'لا محادثات مفتوحة حالياً.' : 'افتح تذكرة جديدة من الأعلى.'} />
        ) : (
          <EntityList>
            {tickets.map((t, i) => (
              <EntityCard
                key={str(t.id, String(i))}
                title={str(t.subject ?? t.title)}
                meta={
                  isAdmin
                    ? `${str(t.user_name)} · ${str(t.user_phone)} · ${formatDateTime(t.created_at)}`
                    : formatDateTime(t.created_at)
                }
                status={t.status}
                onClick={() => void openTicket(t)}
              />
            ))}
          </EntityList>
        )}
      </SectionCard>

      {active ? (
        <SectionCard
          title={str(active.subject, 'المحادثة')}
          subtitle={
            isAdmin
              ? `${str(active.user_name)} · ${str(active.user_phone)}`
              : undefined
          }
          actions={<StatusBadge status={active.status} />}
        >
          <div className="messages support-thread">
            {messages.length === 0 ? (
              <Empty title="لا رسائل بعد" />
            ) : (
              messages.map((m, i) => (
                <article key={str(m.id, String(i))} className="message list-card compact">
                  <div className="list-head">
                    <strong>{Number(m.is_staff) === 1 ? 'الدعم' : str(m.sender_name, 'الزبون')}</strong>
                    <small>{formatDateTime(m.created_at)}</small>
                  </div>
                  <p>{str(m.message ?? m.body)}</p>
                </article>
              ))
            )}
          </div>
          <Form onSubmit={() => void sendReply()}>
            <Textarea label="رد" value={reply} onChange={setReply} required />
            <div className="action-row wrap">
              <Button type="submit" disabled={busy}>
                إرسال الرد
              </Button>
              <Button type="button" variant="secondary" onClick={() => setActive(null)}>
                إغلاق المحادثة
              </Button>
            </div>
          </Form>
        </SectionCard>
      ) : null}
    </ScreenFrame>
  );
}
